// Lo que dice GeoVision, en palabras de Centro SURA; fechas y horas; cómo se arman las puertas.

// ---------------------------------------------------------------- fechas y horas

// El backend manda hora de Venezuela sin zona ("2026-10-23T19:56:11"): se lee tal cual
const partes = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(iso || '');
    return m ? m.slice(1).map((x) => Number(x || 0)) : null;
};

export const hora = (iso, conSegundos = false) => {
    const p = partes(iso);
    if (!p) return '';
    const [, , , h, mi, s] = p;
    const seg = conSegundos ? `:${String(s).padStart(2, '0')}` : '';
    return `${h % 12 || 12}:${String(mi).padStart(2, '0')}${seg} ${h < 12 ? 'a. m.' : 'p. m.'}`;
};

export const fecha = (iso) => {
    const p = partes(iso);
    return p ? `${String(p[2]).padStart(2, '0')}/${String(p[1]).padStart(2, '0')}/${p[0]}` : '';
};

const isoDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const hoyISO = () => isoDe(new Date());
const ayerISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return isoDe(d);
};

// "a las 7:31 a. m." si fue hoy; "ayer a las ..."; "el 21/10 a las ..."
export const cuando = (iso) => {
    const dia = (iso || '').slice(0, 10);
    if (dia === hoyISO()) return `a las ${hora(iso)}`;
    if (dia === ayerISO()) return `ayer a las ${hora(iso)}`;
    return `el ${fecha(iso).slice(0, 5)} a las ${hora(iso)}`;
};

// Para listas: "7:31 a. m." hoy; "22/10, 7:23 a. m." otro día
export const momento = (iso) => ((iso || '').slice(0, 10) === hoyISO() ? hora(iso) : `${fecha(iso).slice(0, 5)}, ${hora(iso)}`);

// ---------------------------------------------------------------- puertas

// La santamaría son dos "puertas" de GeoVision: con la IN se abre (en la mañana) y con la
// OUT se cierra (en la noche). Los ascensores son puertas normales.
export const esSantamaria = (p) => /^santa\s*maria/i.test(p?.nombre || p?.puerta || '');
const esLadoAbre = (texto) => /\sIN$/i.test(texto || '');

// "ASC P6" -> "Ascensor P6"
export const nombrePuerta = (nombre) => {
    if (/^santa\s*maria/i.test(nombre || '')) return 'Santamaría';
    const asc = /^asc\w*\s+P\s?(\d+)$/i.exec(nombre || '');
    return asc ? `Ascensor P${asc[1]}` : nombre;
};

// Lo que muestra la pantalla: la santamaría (sus dos puertas juntas) y cada ascensor
export const armarElementos = (puertas, ultimos) => {
    const santa = puertas.filter(esSantamaria);
    const otras = puertas.filter((p) => !esSantamaria(p));
    const elementos = [];
    if (santa.length) {
        const eventos = santa.flatMap((p) => ultimos?.[p.id] || [])
            .sort((a, b) => (a.fecha_hora < b.fecha_hora ? 1 : -1));
        elementos.push({
            clave: 'santamaria', tipo: 'santamaria', nombre: 'Santamaría',
            abre: santa.find((p) => esLadoAbre(p.nombre)),
            cierra: santa.find((p) => !esLadoAbre(p.nombre)),
            puertas: santa, eventos,
        });
    }
    [...otras].sort((a, b) => nombrePuerta(a.nombre).localeCompare(nombrePuerta(b.nombre))).forEach((p) => {
        elementos.push({
            clave: `puerta-${p.id}`, tipo: 'ascensor', nombre: nombrePuerta(p.nombre), puerta: p,
            puertas: [p], eventos: ultimos?.[p.id] || [],
        });
    });
    return elementos;
};

// Abrió o cerró la santamaría: lo dice el último pase (o lo último hecho desde Centro SURA)
const accionaSantamaria = (ev) => (ev.origen === 'pase' && ev.concedido)
    || (ev.origen === 'cs' && ev.accion === 'abrir')
    || ev.tipo === 'desbloqueada';

export const estadoSantamaria = (elemento) => {
    const ultimo = elemento.eventos.find(accionaSantamaria);
    if (!ultimo) return { clave: 'desconocido', ultimo: null };
    return { clave: esLadoAbre(ultimo.puerta) ? 'abierta' : 'cerrada', ultimo };
};

// Estado de un ascensor en palabras y su tono
export const estadoPuerta = (p) => {
    if (!p.en_linea) return { clave: 'apagada', texto: 'Sin conexión con el controlador' };
    if (!p.habilitada) return { clave: 'apagada', texto: 'Deshabilitado en GeoVision' };
    if (p.alertas.includes('FORCED_OPEN')) return { clave: 'forzada', texto: 'Lo abrieron a la fuerza' };
    if (p.alertas.includes('HELD_OPEN')) return { clave: 'quedo_abierta', texto: 'Se quedó abierto' };
    if (p.modo === 'cierre_total') return { clave: 'bloqueada', texto: 'Cierre total' };
    if (p.modo === 'bloqueada') return { clave: 'bloqueada', texto: 'Bloqueado: no abre ni con huella' };
    if (p.modo === 'siempre_abierta') return { clave: 'siempre_abierta', texto: 'Abierto hasta volver a normal' };
    if (p.abierta) return { clave: 'abierta', texto: 'Abierto' };
    return { clave: 'cerrada', texto: 'En normal: abre con huella' };
};

export const TONO = {
    cerrada: 'text-slate-500',
    abierta: 'text-cyan-700',
    siempre_abierta: 'text-cyan-700',
    bloqueada: 'text-slate-800 font-medium',
    quedo_abierta: 'text-amber-700 font-medium',
    forzada: 'text-rose-600 font-medium',
    apagada: 'text-slate-400',
    desconocido: 'text-slate-400',
};

// Lo que se le puede pedir a un ascensor, con sus palabras
export const ACCIONES = {
    abrir: { nombre: 'Abrir un momento', hecho: 'abierto un momento', ayuda: 'Suelta la cerradura unos segundos, como cuando alguien pone la huella.' },
    mantener_abierta: { nombre: 'Mantener abierto', hecho: 'abierto hasta volver a normal', ayuda: 'Queda abierto para todos hasta volver a normal.' },
    bloquear: { nombre: 'Bloquear', hecho: 'bloqueado', ayuda: 'No abre ni con huella hasta volver a normal.' },
    normal: { nombre: 'Volver a normal', hecho: 'en normal', ayuda: 'Abre otra vez solo con huella.' },
};

// ---------------------------------------------------------------- eventos

export const MOVIMIENTO = { Entrar: 'Entró', Salir: 'Salió' };

const EVENTOS_PUERTA = {
    forzada: { texto: 'La abrieron a la fuerza', tono: 'rosa' },
    quedo_abierta: { texto: 'Se quedó abierta', tono: 'ambar' },
    forzada_fin: { texto: 'Volvió a cerrar después de forzarla', tono: 'neutro' },
    quedo_abierta_fin: { texto: 'Por fin la cerraron', tono: 'neutro' },
    abierta: { texto: 'Se abrió', tono: 'neutro' },
    cerrada: { texto: 'Se cerró', tono: 'neutro' },
    desbloqueada: { texto: 'Se abrió sin huella', tono: 'neutro' },
    siempre_abierta: { texto: 'Quedó abierta hasta volver a normal', tono: 'neutro' },
    bloqueo_fijo: { texto: 'Quedó bloqueada', tono: 'neutro' },
    cierre_total: { texto: 'Cierre total', tono: 'neutro' },
    modo_normal: { texto: 'Volvió a normal', tono: 'neutro' },
    huella_desconocida: { texto: 'Huella no registrada', tono: 'rosa' },
    clave_incorrecta: { texto: 'Clave incorrecta en el teclado', tono: 'rosa' },
    boton_fallido: { texto: 'Botón de salida fuera de horario', tono: 'rosa' },
    timbre: { texto: 'Tocaron el timbre', tono: 'neutro' },
};

const ACCION_CS = {
    abrir: 'Lo abrió desde Centro SURA',
    mantener_abierta: 'Lo dejó abierto desde Centro SURA',
    bloquear: 'Lo bloqueó desde Centro SURA',
    normal: 'Lo volvió a normal desde Centro SURA',
};

// "Acceso negado: Horario inválido" -> "horario inválido"
const motivoNegado = (mensaje) => {
    const m = (mensaje || '').replace(/^acceso\s+(de)?negado:?\s*/i, '').trim();
    return m ? m.charAt(0).toLowerCase() + m.slice(1) : 'acceso negado';
};

export const tonoEvento = (ev) => {
    if (ev.origen === 'pase') return ev.concedido ? 'normal' : 'rosa';
    if (ev.origen === 'cs') return 'azul';
    return EVENTOS_PUERTA[ev.tipo]?.tono || 'neutro';
};

export const CLASE_TONO = {
    normal: 'text-slate-600',
    azul: 'text-sura-blue',
    rosa: 'text-rose-600',
    ambar: 'text-amber-700',
    neutro: 'text-slate-500',
};

// Qué pasó, en pocas palabras y sin la hora: en la santamaría, abrir y cerrar
export const queEvento = (ev) => {
    const santa = esSantamaria(ev);
    const abre = santa && esLadoAbre(ev.puerta);
    if (ev.origen === 'cs') {
        if (santa && ev.accion === 'abrir') return abre ? 'La abrió desde Centro SURA' : 'La cerró desde Centro SURA';
        return ACCION_CS[ev.accion] || 'Desde Centro SURA';
    }
    if (ev.origen === 'pase') {
        if (!ev.concedido) return `Le negaron el paso: ${motivoNegado(ev.mensaje)}`;
        if (santa) return abre ? 'La abrió' : 'La cerró';
        return MOVIMIENTO[ev.direccion] || 'Pasó';
    }
    if (santa && ev.tipo === 'desbloqueada') return abre ? 'Se abrió sin huella' : 'Se cerró sin huella';
    return EVENTOS_PUERTA[ev.tipo]?.texto || ev.mensaje || 'Evento';
};

// Quién: el nombre de Centro SURA si su cédula está, si no el de GeoVision
export const quienEvento = (ev) => ev.empleado?.nombre || ev.nombre || null;
